import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articlesList } from '@/mocks/articles';
import { productCategories } from '@/mocks/products';
import ProductCarousel from '@/components/feature/ProductCarousel';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Home() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    if (lang && ['en', 'fr', 'da', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // ... rest of the file stays the same
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [animatedSections, setAnimatedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFadeClass = (id: string) =>
    animatedSections.has(id)
      ? 'opacity-100 translate-y-0'
      : 'opacity-0 translate-y-6';

  const heroImage = 'https://readdy.ai/api/search-image?query=Premium%20fashion%20products%20flat%20lay%20on%20clean%20white%20marble%20background%2C%20luxury%20sneakers%20handbags%20watches%20accessories%20arranged%20elegantly%2C%20fashion%20editorial%20photography%20with%20soft%20natural%20lighting%2C%20minimalist%20aesthetic%20warm%20neutral%20tones%2C%20high-end%20product%20photography%20with%20subtle%20shadows&width=1600&height=900&seq=hero-main&orientation=landscape';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="AllChinaBuy Spreadsheet fashion products"
            className="w-full h-full object-cover"
            width={1600}
            height={900}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 container-main text-center py-20 md:py-28">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('hero.h1')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('hero.subtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="flex items-center bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg">
                <i className="ri-search-line text-foreground-400 text-lg ml-4"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="flex-1 px-3 py-4 text-foreground-800 bg-transparent outline-none placeholder:text-foreground-400 text-sm"
                />
                <button className="px-5 py-4 bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors">
                  Search
                </button>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href={`/${currentLang}/spreadsheet/`}
                className="btn-primary px-8 py-4 text-base shadow-lg shadow-primary-500/25"
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white/15 backdrop-blur-sm text-white font-medium whitespace-nowrap transition-all hover:bg-white/25 border border-white/30"
              >
                {t('hero.ctaSecondary')}
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>

            {/* Trust badges */}
            <p className="text-sm text-white/70 tracking-wide">
              {t('hero.trust')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Dropdown Section */}
      <section id="categories-dropdown" data-animate className={`section-padding transition-all duration-700 ${getFadeClass('categories-dropdown')}`}>
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t('categories.title')}
              </h2>
              <p className="text-foreground-600">
                {t('categories.subtitle')}
              </p>
            </div>

            <div className="relative" ref={dropdownRef}>
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-white rounded-xl border-2 border-background-200/70 text-left hover:border-primary-300/60 transition-all"
                  >
                    <span className={selectedCategory ? 'text-foreground-800' : 'text-foreground-400'}>
                      {selectedCategory
                        ? categories.find((c) => c.id === selectedCategory)?.name
                        : t('dropdown.placeholder')}
                    </span>
                    <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-background-200/70 shadow-xl overflow-hidden z-30 max-h-[400px] overflow-y-auto animate-fade-in">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setSelectedCategory(cat.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full px-5 py-3 text-left text-sm transition-colors flex items-center gap-3 ${
                            selectedCategory === cat.id
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-foreground-700 hover:bg-background-100'
                          }`}
                        >
                          <span className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" width={32} height={32} />
                          </span>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href={categories.find((c) => c.id === selectedCategory)?.url || mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-4 bg-accent-500 text-white rounded-xl font-medium whitespace-nowrap hover:bg-accent-600 transition-all shadow-lg shadow-accent-500/20"
                >
                  {t('dropdown.goButton')}
                  <i className="ri-arrow-right-up-line ml-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Category Buttons Grid */}
      <section id="category-grid" data-animate className={`section-padding bg-background-100 transition-all duration-700 ${getFadeClass('category-grid')}`}>
        <div className="container-main">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {categories.slice(0, 10).map((cat, index) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group card p-4 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300 hover:-translate-y-1"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden mb-3">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    width={200}
                    height={200}
                  />
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-800 text-center group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
              </a>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {categories.slice(10).map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white rounded-lg border border-background-200/70 text-sm font-medium text-foreground-700 hover:border-primary-300/60 hover:text-primary-600 transition-all"
              >
                <span className="w-6 h-6 rounded overflow-hidden flex-shrink-0">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" width={24} height={24} />
                </span>
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Product Carousels - NEW */}
      <section id="product-carousels" data-animate className={`section-padding transition-all duration-700 ${getFadeClass('product-carousels')}`}>
        <div className="container-main">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              Featured Products
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              Browse the latest products from the AllChinaBuy spreadsheet. Click any item to view details and purchase on kaspreadsheet.com.
            </p>
          </div>

          {productCategories.map((category) => (
            <ProductCarousel key={category.id} category={category} />
          ))}
        </div>
      </section>

      {/* Main CTAs - AllChinaBuy Spreadsheet & Buy It Now */}
      <section id="main-ctas" data-animate className={`section-padding bg-background-100 transition-all duration-700 ${getFadeClass('main-ctas')}`}>
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            <a
              href={`/${currentLang}/spreadsheet/`}
              className="group card p-8 md:p-10 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 hover:-translate-y-1 border-l-4 border-l-primary-500"
            >
              <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mb-5">
                <i className="ri-table-2 text-2xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3 group-hover:text-primary-600 transition-colors">
                {t('ctas.exploreTitle')}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                {t('ctas.exploreDesc')}
              </p>
              <span className="inline-flex items-center text-primary-600 font-medium text-sm group-hover:gap-2 transition-all">
                {t('ctas.exploreButton')}
                <i className="ri-arrow-right-line ml-1"></i>
              </span>
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="group card p-8 md:p-10 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-300 hover:-translate-y-1 border-l-4 border-l-accent-500"
            >
              <div className="w-14 h-14 rounded-xl bg-accent-50 flex items-center justify-center mb-5">
                <i className="ri-shopping-bag-3-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3 group-hover:text-accent-600 transition-colors">
                {t('ctas.buyTitle')}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                {t('ctas.buyDesc')}
              </p>
              <span className="inline-flex items-center text-accent-600 font-medium text-sm group-hover:gap-2 transition-all">
                {t('ctas.buyButton')}
                <i className="ri-arrow-right-up-line ml-1"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Spreadsheet Explorer Section */}
      <section id="spreadsheet-section" data-animate className={`section-padding transition-all duration-700 ${getFadeClass('spreadsheet-section')}`}>
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {t('spreadsheet.title')}
              </h2>
              <p className="text-foreground-600 mb-8 leading-relaxed">
                {t('spreadsheet.subtitle')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-links-line', text: t('spreadsheet.feature1') },
                  { icon: 'ri-shield-check-line', text: t('spreadsheet.feature2') },
                  { icon: 'ri-refresh-line', text: t('spreadsheet.feature3') },
                  { icon: 'ri-emotion-happy-line', text: t('spreadsheet.feature4') },
                ].map((feature) => (
                  <div key={feature.text} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-background-200/70">
                    <i className={`${feature.icon} text-primary-500 text-lg`}></i>
                    <span className="text-sm font-medium text-foreground-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20spreadsheet%20interface%20design%20on%20laptop%20screen%2C%20clean%20white%20background%20with%20subtle%20green%20and%20warm%20neutral%20accents%2C%20modern%20UI%20design%20showing%20product%20categories%20and%20data%20tables%2C%20professional%20product%20photography%20with%20soft%20natural%20lighting%2C%20editorial%20style%20flat%20lay%20on%20light%20wooden%20desk&width=800&height=600&seq=spreadsheet-preview&orientation=landscape"
                alt="AllChinaBuy Spreadsheet Preview"
                className="w-full rounded-2xl shadow-xl"
                width={800}
                height={600}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section id="guides-section" data-animate className={`section-padding bg-background-100 transition-all duration-700 ${getFadeClass('guides-section')}`}>
        <div className="container-main">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('guides.title')}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              {t('guides.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                title: t('guides.guide1Title'),
                desc: t('guides.guide1Desc'),
                slug: 'spreadsheet',
                icon: 'ri-file-list-3-line',
                color: 'primary',
              },
              {
                title: t('guides.guide2Title'),
                desc: t('guides.guide2Desc'),
                slug: 'guides',
                icon: 'ri-user-search-line',
                color: 'accent',
              },
              {
                title: t('guides.guide3Title'),
                desc: t('guides.guide3Desc'),
                slug: 'shipping',
                icon: 'ri-truck-line',
                color: 'secondary',
              },
              {
                title: t('guides.guide4Title'),
                desc: t('guides.guide4Desc'),
                slug: 'review',
                icon: 'ri-star-line',
                color: 'primary',
              },
            ].map((guide) => (
              <a
                key={guide.slug}
                href={`/${currentLang}/${guide.slug}/`}
                className="group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-xl bg-${guide.color}-50 flex items-center justify-center mb-4`}>
                  <i className={`${guide.icon} text-xl text-${guide.color}-600`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {guide.desc}
                </p>
                <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:gap-2 transition-all">
                  {t('guides.readMore')}
                  <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Search Cards */}
      <section id="search-cards" data-animate className={`section-padding transition-all duration-700 ${getFadeClass('search-cards')}`}>
        <div className="container-main">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('searchCards.title')}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { key: 'spreadsheet', icon: 'ri-table-2', href: `/${currentLang}/spreadsheet/` },
              { key: 'review', icon: 'ri-star-line', href: `/${currentLang}/review/` },
              { key: 'shipping', icon: 'ri-truck-line', href: `/${currentLang}/shipping/` },
              { key: 'coupon', icon: 'ri-ticket-line', href: `/${currentLang}/coupon/` },
              { key: 'discount', icon: 'ri-price-tag-3-line', href: `/${currentLang}/coupon/` },
              { key: 'warehouse', icon: 'ri-store-line', href: `/${currentLang}/shipping/` },
              { key: 'legit', icon: 'ri-shield-check-line', href: `/${currentLang}/safety/` },
              { key: 'qc', icon: 'ri-camera-line', href: `/${currentLang}/safety/` },
            ].map((card) => (
              <a
                key={card.key}
                href={card.href}
                className="group flex items-center gap-3 p-4 bg-white rounded-xl border border-background-200/70 hover:border-primary-300/60 hover:shadow-md transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className={`${card.icon} text-primary-600`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">
                  {t(`searchCards.${card.key}`)}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section id="trust-section" data-animate className={`section-padding bg-background-100 transition-all duration-700 ${getFadeClass('trust-section')}`}>
        <div className="container-main">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('trust.title')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { key: 'independent', icon: 'ri-shield-check-line' },
              { key: 'updated', icon: 'ri-refresh-line' },
              { key: 'beginner', icon: 'ri-emotion-happy-line' },
            ].map((item) => (
              <div key={item.key} className="text-center p-6">
                <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">
                  {t(`trust.${item.key}`)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(`trust.${item.key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety-section" data-animate className={`section-padding transition-all duration-700 ${getFadeClass('safety-section')}`}>
        <div className="container-main">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('safety.title')}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              {t('safety.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { key: 'payment', icon: 'ri-bank-card-line' },
              { key: 'verification', icon: 'ri-user-search-line' },
              { key: 'qc', icon: 'ri-camera-line' },
              { key: 'scams', icon: 'ri-alarm-warning-line' },
            ].map((item) => (
              <div key={item.key} className="card p-6">
                <div className="w-12 h-12 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">
                  {t(`safety.${item.key}`)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(`safety.${item.key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" data-animate className={`section-padding bg-background-100 transition-all duration-700 ${getFadeClass('faq-section')}`}>
        <div className="container-main max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('faq.title')}
            </h2>
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="card p-6">
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-primary-500 mt-1 flex-shrink-0"></i>
                  {t(`faq.q${num}`)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed pl-7">
                  {t(`faq.a${num}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" data-animate className={`section-padding bg-primary-500 transition-all duration-700 ${getFadeClass('final-cta')}`}>
        <div className="container-main text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            {t('finalCta.title')}
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            {t('finalCta.subtitle')}
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center px-8 py-4 bg-white text-primary-700 rounded-xl font-semibold whitespace-nowrap transition-all hover:bg-white/90 shadow-lg"
          >
            {t('finalCta.button')}
            <i className="ri-arrow-right-up-line ml-2"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}